import { useEffect, useRef } from "react";

type AutoLoadArgs = {
  /** Scrollable listbox container (the element that actually scrolls) */
  listboxRef: React.RefObject<HTMLElement | null>;
  /** A sentinel element rendered at the very bottom (e.g. around your “Load more” button) */
  sentinelRef: React.RefObject<HTMLElement | null>;
  /** Whether the popup/list is open/visible */
  isOpen: boolean;
  /** Whether there are more pages */
  hasMore: boolean;
  /** Whether a page is currently loading */
  isLoading: boolean;
  /** Your load-more function (can be sync or async) */
  onLoadMore: () => void | Promise<void>;

  /** Optional tuning */
  cooldownMs?: number; // min spacing between auto loads
  rootMargin?: string; // IO rootMargin
  nudgeRatio?: number; // how much to scroll up when still pinned at bottom
  deps?: any[]; // extra deps to re-bind IO (e.g. items.length)
};

export function useAutoLoadList({
  listboxRef,
  sentinelRef,
  isOpen,
  hasMore,
  isLoading,
  onLoadMore,
  cooldownMs = 600,
  rootMargin = "0px 0px 32px 0px",
  nudgeRatio = 0.33,
  deps = [],
}: AutoLoadArgs) {
  const inViewRef = useRef(false);
  const autoInFlight = useRef(false);
  const cooldownUntil = useRef(0);

  const resolveRoot = () => {
    const root = listboxRef.current;
    const sentinel = sentinelRef.current;
    if (!sentinel) return root || null;
    // if portals/wrappers are involved, try climbing to the real listbox
    if (root && root.contains(sentinel)) return root;
    return (
      sentinel.closest<HTMLElement>('[data-slot="listbox"]') ||
      sentinel.closest<HTMLElement>('[role="listbox"]') ||
      root ||
      null
    );
  };

  const nudgeIfPinned = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const root = resolveRoot();
        if (!root) return;
        const remaining =
          root.scrollHeight - (root.scrollTop + root.clientHeight);
        if (remaining <= 4) {
          root.scrollTop = Math.max(
            0,
            root.scrollTop - Math.ceil(root.clientHeight * nudgeRatio),
          );
        }
      });
    });
  };

  const runAutoLoad = async () => {
    if (autoInFlight.current) return;
    if (Date.now() < cooldownUntil.current) return;
    autoInFlight.current = true;
    try {
      await Promise.resolve(onLoadMore());
    } finally {
      autoInFlight.current = false;
      cooldownUntil.current = Date.now() + cooldownMs;
      nudgeIfPinned();
    }
  };

  // Attach IO + a lightweight scroll re-check
  useEffect(() => {
    if (!isOpen || !hasMore) return;
    const root = resolveRoot();
    const target = sentinelRef.current;
    if (!root || !target) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = !!entry?.isIntersecting;
        if (inViewRef.current && !isLoading) {
          // try once immediately (e.g. open exactly at bottom)
          runAutoLoad();
        }
      },
      { root, threshold: 0.01, rootMargin },
    );

    io.observe(target);

    const onScroll = () => {
      // If user keeps scrolling while sentinel is visible, allow one per cooldown
      if (inViewRef.current && !isLoading && hasMore) {
        runAutoLoad();
      }
    };
    root.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      io.disconnect();
      root.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasMore, isLoading, ...deps]);

  // Reset guards when closing or when caller changes inputs (e.g. new query)
  useEffect(() => {
    if (!isOpen) {
      autoInFlight.current = false;
      cooldownUntil.current = 0;
      inViewRef.current = false;
    }
  }, [isOpen]);
}
